/** Which right lets a person SEE each screen — for the menu. RIGHTS_MODEL.md §6.1.
 *
 * Declared screens (masters, documents, stores) carry their own module and
 * object, so their `view` permission is read from the declaration and cannot
 * drift from it. The handful of bespoke screens are listed by hand, each with
 * the check its own endpoint makes.
 *
 * The menu hides a screen only when this says no. A built screen missing from
 * here would stay visible — and `access.test.ts` fails if one is.
 */

import type { Rights } from '@/lib/rights'
import { MASTER_SCREENS } from './registry'
import { DOCUMENT_SCREENS } from './documents'
import { STORES_DOCS } from './stores'

const BESPOKE: Record<string, (r: Rights) => boolean> = {
  // The queue lists only what the person may approve, so it is worth a menu
  // entry to anyone who may approve anything at all.
  'approvals': r => r.canAny('.approve'),
  'admin/rights': r => r.can('admin.role.view') || r.can('admin.user.view'),
  'masters/item-routes': r => r.can('masters.item_route.view'),
  'masters/bom': r => r.can('masters.bom.view'),
  'jobwork/order': r => r.can('jobwork.order.view'),
  'design/item-revisions': r => r.can('design.item_revision.view'),
  'stores/balance': r => r.can('stores.stock_balance.view'),
}

/** undefined = this screen has no mapping (the test forbids that for any
 *  built screen); true/false = may / may not view. */
export function canView(screen: string, r: Rights): boolean | undefined {
  if (BESPOKE[screen]) return BESPOKE[screen](r)
  const spec = MASTER_SCREENS[screen] ?? DOCUMENT_SCREENS[screen]
  if (spec) return r.can(`${spec.module}.${spec.object}.view`)
  const stores = STORES_DOCS[screen]
  if (stores) return r.can(`stores.${stores.object}.view`)
  return undefined
}
